import { FanguardApiContractsClientAssetModel } from 'src/api/generated';
import { FileData } from 'src/pages/Devices/GroupDetails/TabPages/GroupDesign/components/FileUpload/FileUpload';
import { sanitizeFileName } from 'src/utils/sanitize';

const PREVIEWABLE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.svg', '.gif', '.webp'];

export const getAssetExtension = (asset: FanguardApiContractsClientAssetModel): string => {
  const name = asset.name || asset.url || '';
  const withoutQuery = name.split('?')[0];
  const dotIndex = withoutQuery.lastIndexOf('.');

  return dotIndex === -1 ? '' : withoutQuery.slice(dotIndex).toLowerCase();
};

export const isPreviewableAsset = (asset: FanguardApiContractsClientAssetModel): boolean =>
  PREVIEWABLE_EXTENSIONS.includes(getAssetExtension(asset));

export const filterAssetsByAccept = (
  assets: FanguardApiContractsClientAssetModel[],
  accept?: string,
): FanguardApiContractsClientAssetModel[] => {
  if (!accept) return assets;

  const allowedExtensions = accept
    .split(',')
    .map((type) => type.trim().toLowerCase())
    .filter((type) => type.startsWith('.'));

  if (!allowedExtensions.length) return assets;

  return assets.filter((asset) => allowedExtensions.includes(getAssetExtension(asset)));
};

// Maps a stored kiosk image model onto the shape the upload field renders. The API returns the file
// under `url`, but a value that has just been picked in the form only carries the base64 in `image`.
export const imageModelToFileData = (value: unknown): FileData | undefined => {
  if (!value) return;
  if (typeof value === 'string') return { url: value };
  if (typeof value !== 'object') return;

  const imageObject = value as Record<string, unknown>;

  if (imageObject.delete) return;

  const fileData: FileData = {};

  if (typeof imageObject.url === 'string' && imageObject.url) {
    fileData.url = imageObject.url;
  } else if (typeof imageObject.image === 'string' && imageObject.image) {
    const image = imageObject.image;
    fileData.url = image.startsWith('data:') || image.startsWith('http') ? image : `data:image/png;base64,${image}`;
  }

  if (!fileData.url) return;

  if (typeof imageObject.name === 'string') {
    fileData.name = imageObject.name;
  }

  if (typeof imageObject.size === 'string' || typeof imageObject.size === 'number') {
    fileData.size = imageObject.size;
  }

  return fileData;
};

// Fields that only store an asset id never need the bytes, so the remote URL is enough to preview.
export const assetToPreviewFileData = (asset: FanguardApiContractsClientAssetModel): FileData => ({
  url: asset.url || '',
  name: sanitizeFileName(asset.name || ''),
  size: asset.size,
});

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('Unable to read asset'));
    reader.readAsDataURL(blob);
  });

export interface AssetFileDataResult {
  fileData: FileData;
  // False when the bytes could not be inlined, so the payload carries only the remote URL
  // and depends on the backend resolving it.
  isEmbedded: boolean;
}

export const assetToFileData = async (asset: FanguardApiContractsClientAssetModel): Promise<AssetFileDataResult> => {
  const name = sanitizeFileName(asset.name || '');
  const url = asset.url || '';

  if (!url) {
    return { fileData: { name, size: asset.size }, isEmbedded: false };
  }

  if (url.startsWith('data:')) {
    return { fileData: { url, image: url, name, size: asset.size }, isEmbedded: true };
  }

  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Asset request failed with status ${response.status}`);
    }

    const blob = await response.blob();
    const dataUrl = await blobToDataUrl(blob);

    return { fileData: { url: dataUrl, image: dataUrl, name, size: blob.size }, isEmbedded: true };
  } catch (error) {
    console.warn('Unable to inline asset bytes, falling back to the asset URL.', error);

    return { fileData: { url, name, size: asset.size }, isEmbedded: false };
  }
};
